import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import DashboardsContext from 'contexts/DashboardsContext';
import ReportsContext from 'contexts/ReportsContext';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import Row from 'components/Common/Row';
import Inputs from 'components/Inputs';
import Search from 'components/Search';
import AccessInput from 'components/AccessInput';
import { InsightsChartSpec } from 'services/streamInsightsQuery';

import useSaveInsightsChart, {
  InsightsChartSaveOverrides,
} from './useSaveInsightsChart';

type Destination =
  | 'this-dashboard'
  | 'this-report'
  | 'new-dashboard'
  | 'existing-dashboard'
  | 'new-report'
  | 'existing-report';

const List = styled.div`
  margin-bottom: 24px;
  height: 320px;
  overflow-y: auto;
  padding-right: 12px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  height: 32px;
  &:hover {
    background-color: var(--color-bg-hover);
  }
`;

// Two-step "save chart to a dashboard/report" flow, mirroring
// CopyDashboardGadgetToReportButton. Step 1 picks the destination; "this …"
// saves immediately, the others advance to a name/select step.
const SaveInsightsChartModal = ({
  spec,
  overrides,
  onClose,
}: {
  spec: InsightsChartSpec;
  // Present when the save came from the Explore modal: what the user drilled
  // into, reshaped and re-dated there, rather than the chat's original chart.
  overrides?: InsightsChartSaveOverrides;
  onClose: () => void;
}) => {
  const { allDashboards } = useContext(DashboardsContext);
  const { availableReports } = useContext(ReportsContext);
  const { activeEntity } = useContext(InsightsChatContext);
  const {
    isSaving,
    saveToNewDashboard,
    saveToExistingDashboard,
    saveToNewReport,
    saveToExistingReport,
  } = useSaveInsightsChart(spec, overrides);

  const currentDashboard = useMemo(
    () =>
      activeEntity?.entityType === 'dashboard'
        ? allDashboards.find((d) => d.id === activeEntity.entityId)
        : undefined,
    [activeEntity, allDashboards],
  );
  const currentReport = useMemo(
    () =>
      activeEntity?.entityType === 'report'
        ? availableReports.find((r) => r.id === activeEntity.entityId)
        : undefined,
    [activeEntity, availableReports],
  );

  const [step, setStep] = useState<1 | 2>(1);
  const [destination, setDestination] = useState<Destination | undefined>();
  const [title, setTitle] = useState('');
  const [access, setAccess] = useState<ResourceAccess>({
    version: '2',
    type: 'Private',
  });
  const [searchText, setSearchText] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState<
    DashboardType | undefined
  >();
  const [selectedReport, setSelectedReport] = useState<
    ReportType | undefined
  >();

  const filteredDashboards = useMemo(() => {
    const lower = searchText.toLowerCase();
    return allDashboards
      .filter((d) => !d.isWallboardSlide && !d.templateId)
      .filter((d) => d.name.toLowerCase().includes(lower));
  }, [allDashboards, searchText]);
  const filteredReports = useMemo(() => {
    const lower = searchText.toLowerCase();
    return availableReports.filter((r) => r.name.toLowerCase().includes(lower));
  }, [availableReports, searchText]);

  const radioOptions: RadioOption[] = useMemo(() => {
    const options: RadioOption[] = [];
    if (currentDashboard) {
      options.push({
        key: 'this-dashboard',
        label: `This dashboard (${currentDashboard.name})`,
        isSelected: destination === 'this-dashboard',
        onSelected: () => setDestination('this-dashboard'),
        testId: 'save-to-this-dashboard',
      });
    }
    if (currentReport) {
      options.push({
        key: 'this-report',
        label: `This report (${currentReport.name})`,
        isSelected: destination === 'this-report',
        onSelected: () => setDestination('this-report'),
        testId: 'save-to-this-report',
      });
    }
    // New/Existing report destinations are intentionally hidden — charts save
    // to dashboards (and "this report" when one is being viewed) only.
    options.push(
      {
        key: 'new-dashboard',
        label: 'New dashboard',
        isSelected: destination === 'new-dashboard',
        onSelected: () => setDestination('new-dashboard'),
        testId: 'save-to-new-dashboard',
      },
      {
        key: 'existing-dashboard',
        label: 'Existing dashboard',
        isSelected: destination === 'existing-dashboard',
        onSelected: () => setDestination('existing-dashboard'),
        testId: 'save-to-existing-dashboard',
      },
    );
    return options;
  }, [currentDashboard, currentReport, destination]);

  const closeIfSaved = useCallback(
    (ok: boolean) => {
      if (ok) {
        onClose();
      }
    },
    [onClose],
  );

  const onContinue = useCallback(async () => {
    if (destination === 'this-dashboard' && currentDashboard) {
      closeIfSaved(await saveToExistingDashboard(currentDashboard));
    } else if (destination === 'this-report' && currentReport) {
      closeIfSaved(await saveToExistingReport(currentReport));
    } else if (destination) {
      setStep(2);
    }
  }, [
    destination,
    currentDashboard,
    currentReport,
    saveToExistingDashboard,
    saveToExistingReport,
    closeIfSaved,
  ]);

  const onSave = useCallback(async () => {
    if (destination === 'new-dashboard') {
      closeIfSaved(await saveToNewDashboard({ name: title, access }));
    } else if (destination === 'new-report') {
      closeIfSaved(await saveToNewReport({ name: title, access }));
    } else if (destination === 'existing-dashboard' && selectedDashboard) {
      closeIfSaved(await saveToExistingDashboard(selectedDashboard));
    } else if (destination === 'existing-report' && selectedReport) {
      closeIfSaved(await saveToExistingReport(selectedReport));
    }
  }, [
    destination,
    title,
    access,
    selectedDashboard,
    selectedReport,
    saveToNewDashboard,
    saveToNewReport,
    saveToExistingDashboard,
    saveToExistingReport,
    closeIfSaved,
  ]);

  const onBack = useCallback(() => {
    setStep(1);
    setSearchText('');
    setSelectedDashboard(undefined);
    setSelectedReport(undefined);
    setTitle('');
  }, []);

  const isNew = destination === 'new-dashboard' || destination === 'new-report';
  const isExisting =
    destination === 'existing-dashboard' || destination === 'existing-report';
  const existingItems: (DashboardType | ReportType)[] =
    destination === 'existing-dashboard' ? filteredDashboards : filteredReports;
  const selectedExistingId =
    destination === 'existing-dashboard'
      ? selectedDashboard?.id
      : selectedReport?.id;

  return (
    <Modal onClose={onClose} shouldScrollInViewport={false} autoFocus={false}>
      {step === 1 && (
        <Form data-testid="save-insights-chart-flow">
          <FormHeader title="Save chart to" onClose={onClose} />
          <FormContent>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body type="Label">Destination</Typography.Body>
              </div>
              <Inputs.Radio options={radioOptions} />
            </div>
          </FormContent>
          <Row
            style={{ justifyContent: 'flex-end', marginBottom: 18 }}
            centerAlign
          >
            <div style={{ marginRight: 12 }}>
              <Button
                onClick={onClose}
                size="Small"
                type="Ghost"
                label="Cancel"
                isDisabled={isSaving}
              />
            </div>
            <Button
              type="Primary"
              size="Small"
              label={destination?.startsWith('this-') ? 'Save' : 'Continue'}
              onClick={onContinue}
              isDisabled={destination === undefined || isSaving}
              isLoading={isSaving}
            />
          </Row>
        </Form>
      )}
      {step === 2 && isNew && (
        <Form>
          <FormHeader
            title={
              destination === 'new-dashboard'
                ? 'Create dashboard'
                : 'Create report'
            }
            onClose={onClose}
          />
          <FormContent>
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">Title</Typography.Body>
              <Inputs.TextInput
                value={title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setTitle(e.target.value)
                }
                data-testid="save-chart-title"
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <AccessInput
                access={access}
                setAccess={setAccess}
                canSetToPrivate
              />
            </div>
          </FormContent>
          <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
            <Button
              onClick={onClose}
              isDisabled={isSaving}
              type="Ghost"
              size="Small"
              label="Cancel"
            />
            <Row centerAlign>
              <div style={{ marginRight: 12 }}>
                <Button
                  type="Ghost"
                  size="Small"
                  onClick={onBack}
                  isDisabled={isSaving}
                  label="Back"
                />
              </div>
              <Button
                type="Primary"
                size="Small"
                label="Save"
                onClick={onSave}
                isLoading={isSaving}
                isDisabled={title === '' || isSaving}
              />
            </Row>
          </Row>
        </Form>
      )}
      {step === 2 && isExisting && (
        <Form data-testid="save-insights-chart-flow">
          <FormHeader
            title={
              destination === 'existing-dashboard'
                ? 'Select dashboard'
                : 'Select report'
            }
            onClose={onClose}
          />
          <FormContent>
            <div style={{ marginBottom: 16 }}>
              <Search
                noBorder
                onSearchTextChanged={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchText(e.target.value)
                }
                searchText={searchText}
                onClearClicked={() => setSearchText('')}
              />
            </div>
            <List>
              {existingItems.map((item) => (
                <Item
                  key={item.id}
                  onClick={() => {
                    if (destination === 'existing-dashboard') {
                      setSelectedDashboard(
                        selectedDashboard?.id === item.id
                          ? undefined
                          : (item as DashboardType),
                      );
                    } else {
                      setSelectedReport(
                        selectedReport?.id === item.id
                          ? undefined
                          : (item as ReportType),
                      );
                    }
                  }}
                >
                  <Row
                    centerAlign
                    style={{ justifyContent: 'space-between', width: '100%' }}
                  >
                    <Typography.Body type="Body 12">
                      {item.name}
                    </Typography.Body>
                    {selectedExistingId === item.id && (
                      <Icon icon="checkmark" />
                    )}
                  </Row>
                </Item>
              ))}
            </List>
          </FormContent>
          <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
            <Button
              onClick={onClose}
              isDisabled={isSaving}
              label="Cancel"
              size="Small"
              type="Ghost"
            />
            <Row centerAlign>
              <div style={{ marginRight: 12 }}>
                <Button
                  onClick={onBack}
                  isDisabled={isSaving}
                  label="Back"
                  size="Small"
                  type="Ghost"
                />
              </div>
              <Button
                type="Primary"
                size="Small"
                onClick={onSave}
                isDisabled={isSaving || !selectedExistingId}
                isLoading={isSaving}
                label="Save"
              />
            </Row>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default SaveInsightsChartModal;
